import { PrivilegesEnum } from 'enums/privileges'
import { useHandlerPrivileges } from 'hooks/useHandlerPrivileges'
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Rotas } from 'routes/rotas'
import useContratoInfoStore from 'store/useContrato'
import useGlobalStore from 'store/useGlobal'
import { HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import { useCreditoAnaliseStore } from 'store/useCreditoAnaliseStore'
import { UserPrivileges } from 'types/usuario' // Certifique-se de que o tipo UserPrivileges está importado corretamente
import { useSnackbarAlertContext } from './SnackbarContext'

type SimulationAuthContextType = {
  simulationFlex: boolean
  setSimulationFlex: React.Dispatch<React.SetStateAction<boolean>>
  validateSimulationFlexStatus: () => void
  isCreditoAnalisado: boolean
  setIsCreditoAnalisado: (value: boolean) => void
}

type SimulationAuthProviderProps = {
  children: React.ReactNode
}

const SimulationAuthContext = createContext<SimulationAuthContextType | undefined>(undefined)

export const SimulationAuthProvider = ({ children }: SimulationAuthProviderProps) => {
  const [simulationFlex, setSimulationFlex] = useState(false)
  const { processedProposal } = useContratoInfoStore((state) => state)
  const { guid } = useGlobalStore((state) => state)
  const { isCreditoAnalisado, setIsCreditoAnalisado } = useCreditoAnaliseStore((state) => state)
  const handlerPrivileges = useHandlerPrivileges()
  const navigate = useNavigate()
  const { showAlert } = useSnackbarAlertContext()

  const currentUrl = window.location.href

  const isShoppingArea = useMemo(
    () =>
      [
        Rotas.INICIO,
        Rotas.GRUPOS,
        Rotas.PRODUTOS,
        Rotas.DETALHE_PRODUTO,
        Rotas.CARRINHO,
        Rotas.CONTRATO_DIGITAL,
        Rotas.CONTRATO_MANUAL,
        Rotas.CREDITO_STATUS
      ].some((route) => currentUrl.includes(route)),
    [currentUrl]
  )

  const isContractPage = useMemo(
    () =>
      (processedProposal?.id_documento && currentUrl.includes(Rotas.CONTRATO_MANUAL)) ||
      currentUrl.includes(Rotas.CONTRATO_DIGITAL),
    [processedProposal, currentUrl]
  )

  const isCreditRoutNotSafe = useMemo(
    () => !currentUrl.includes(Rotas.INICIO) && !currentUrl.includes(Rotas.CREDITO_STATUS),
    [currentUrl]
  )

  const validateSimulationFlexStatus = useCallback(() => {
    if (isCreditRoutNotSafe && guid === 'DEFAULT_GUID') {
      navigate(Rotas.MENU)
    }
  }, [isCreditRoutNotSafe, guid, navigate])

  useEffect(() => {
    const validatePrivileges = () => {
      if (isCreditRoutNotSafe) {
        // Realizamos o cast de PrivilegesEnum.sn_simulacao para garantir que é uma chave válida
        handlerPrivileges(PrivilegesEnum.sn_simulacao as keyof UserPrivileges)

        if (!isContractPage) {
          validateSimulationFlexStatus()
        }
      }
    }

    validatePrivileges()
  }, [isCreditRoutNotSafe, isContractPage, validateSimulationFlexStatus])

  useEffect(() => {
    if (!isShoppingArea && !isCreditoAnalisado && guid !== 'DEFAULT_GUID') {
      HandlerErrorMessage({
        showAlert,
        customErrorMessage:
          'Crédito ainda não foi analisado. Por favor, siga o fluxo de análise de crédito.'
      })

      navigate(Rotas.CARRINHO)
    }
  }, [isShoppingArea, isCreditoAnalisado, navigate])

  const handleCreditoAnalisadoUpdate = (value: boolean | ((prevState: boolean) => boolean)) => {
    if (typeof value === 'function') {
      setIsCreditoAnalisado(value(isCreditoAnalisado)) // Passa o estado atual
    } else {
      setIsCreditoAnalisado(value)
    }
  }

  return (
    <SimulationAuthContext.Provider
      value={{
        simulationFlex,
        setSimulationFlex,
        validateSimulationFlexStatus,
        isCreditoAnalisado,
        setIsCreditoAnalisado: handleCreditoAnalisadoUpdate
      }}
    >
      {children}
    </SimulationAuthContext.Provider>
  )
}

export const useSimulationAuth = () => {
  const context = useContext(SimulationAuthContext)

  if (context === undefined) {
    throw new Error('useSimulationAuth must be used within a SimulationAuthProvider')
  }

  return context
}
